import {
  Box,
  Button,
  SwipeableDrawer,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { forwardRef, useImperativeHandle, useState } from "react";
import { useForm } from "react-hook-form";
import { titleStr } from "../lib/helper";
import FormDateRange from "./Form/FormDateRange";
import FormSelection from "./Form/FormSelection";
import { SelectionOption } from "./Form/FormSelection";
import { uniqBy } from "lodash";
import { useConfig } from "../containers/ConfigContext";

export interface FilterGroup<T> {
  title: string;
  items: FilterItem<T>[];
}

export interface FilterItem<T> {
  name: keyof T;
  label?: string;
  type: FilterType;
  format?: string;
  options?: SelectionOption[];
  multiple?: boolean;
  valueMap?: (value: T[keyof T], record: T) => string;
}

export enum FilterType {
  "date_range" = "date_range",
  "selection" = "selection",
}

const TableFilter = <T extends unknown>(
  props: {
    filters: FilterGroup<T>[];
    allData: T[];
    onFilter: ((data: any) => void) | ((data: any) => Promise<void>);
  },
  ref: any
) => {
  const [open, setOpen] = useState<boolean>(false);

  useImperativeHandle(ref, () => ({
    open() {
      setOpen(true);
    },
  }));
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  const { handleSubmit, control, reset, setError } = useForm();
  const { localized } = useConfig();

  const onSubmit = async (data: any) => {
    if (data) {
      data = Object.fromEntries(
        Object.entries(data).filter(([_, v]) => !(Array.isArray(v) && v.length === 0))
      );
    }
    await props.onFilter(data);
    setOpen(false);
  };

  return (
    <SwipeableDrawer
      anchor="right"
      open={open}
      onClose={() => setOpen(false)}
      onOpen={() => setOpen(true)}
    >
      <Box
        className="padding-big height-100p"
        sx={{ width: fullScreen ? "90vw" : "30vw", maxWidth: 600 }}
      >
        <div className="column-wrapper between-wrapper height-100p">
          <div className="width-100p">
            {props.filters.map((f) => (
              <div key={f.title}>
                <Typography className="margin-b-normal" variant="h6">
                  {f.title}
                </Typography>
                {f.items.map((item) => {
                  let comp = null;
                  if (item.type === FilterType.date_range) {
                    comp = (
                      <FormDateRange
                        control={control}
                        setError={setError}
                        name={item.name.toString()}
                        label={localized(
                          titleStr(item.label || item.name.toString())
                        )}
                      />
                    );
                  } else if (item.type === FilterType.selection) {
                    comp = (
                      <FormSelection
                        noEmpty
                        multiple={item.multiple}
                        control={control}
                        setError={setError}
                        name={item.name.toString()}
                        label={localized(
                          titleStr(item.label || item.name.toString())
                        )}
                        options={
                          item.options ||
                          uniqBy(props.allData, item.name).map((a) => ({
                            label: titleStr(
                              `${item.valueMap
                                ? item.valueMap(a[item.name], a)
                                : a[item.name]
                              }`
                            ),
                            value: `${a[item.name]}`,
                          }))
                        }
                      />
                    );
                  }
                  return (
                    <div key={item.name.toString()} className="margin-b-normal">
                      {comp}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
          <div className="width-100p center-wrapper between-wrapper">
            <Button
              color="info"
              onClick={() => {
                reset();
                onSubmit(null);
              }}
              variant="contained"
            >
              {localized("Reset")}
            </Button>
            <Button variant="contained" onClick={handleSubmit(onSubmit)}>
              {localized("Apply")}
            </Button>
          </div>
        </div>
      </Box>
    </SwipeableDrawer>
  );
};

export default forwardRef(TableFilter) as <T extends unknown>(props: {
  filters: FilterGroup<T>[];
  allData: any[];
  onFilter: (data: any) => void;
  ref: any;
}) => JSX.Element;
